import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { toast } from 'react-toastify';
import {
  AlignCenterHorizontal,
  AlignCenterVertical,
  AlignEndHorizontal,
  AlignEndVertical,
  AlignHorizontalDistributeCenter,
  AlignStartHorizontal,
  AlignStartVertical,
  AlignVerticalDistributeCenter,
  ArrowLeft,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Eye,
  GripVertical,
  Layers,
  Loader2,
  MousePointerClick,
  PenLine,
  Pencil,
  Plus,
  Save,
  Trash2,
  X,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';

import { cn } from '../../lib/utils';
import { loadPdfDocument, renderPageToCanvas } from '../../lib/pdfRender';
import SignDocument from './SignDocument';
import SignatureModal from './SignatureModal';
import {
  fetchTemplatePreviewUrl,
  useFetchTemplate,
  useFetchTemplatePdfUrl,
  useUpdateTemplate,
} from '../../services/documents';
import {
  FIELD_TYPE_GROUPS,
  fieldTypeMeta,
  TEXTUAL_TYPES,
  defaultSizeFor,
  linesForBox,
  newFieldId,
  parsePlacements,
  groupMembersOrdered,
  DIGIT_CELLS_MAX,
  DIGIT_CELLS_MIN,
  DIGIT_GAP_MAX,
  DIGIT_PRESETS,
  digitBoxLayout,
  requiredMark,
  digitCellsOf,
  digitSegmentsOf,
} from './documentHelpers';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';

const RENDER_WIDTH = 720;
const PLACEHOLDER_H = Math.round((RENDER_WIDTH * 11) / 8.5);
const ZOOM_MIN = 0.5;
const ZOOM_MAX = 2.5;
const ZOOM_STEP = 0.25;
const clamp01 = (n) => Math.min(1, Math.max(0, n));

const MiniBtn = ({ onClick, disabled, title, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    className="flex h-7 w-7 items-center justify-center rounded text-foreground hover:bg-accent disabled:opacity-40"
  >
    {children}
  </button>
);

// Conditional-rule control (Enable-when / Required-when). Click-to-link: rather
// than a dropdown of (often unnamed) fields, the admin clicks the trigger field
// on the form. Shows the current rule + Pick/Change + Clear.
const RulePicker = ({ summaryPrefix, emptyText, rule, triggerLabel, isLinking, onPick, onClear }) => (
  <div className="space-y-1.5">
    <div className="rounded-md border border-input bg-background px-2 py-1.5 text-xs text-foreground">
      {rule ? (
        <>
          {summaryPrefix} <span className="font-medium">{triggerLabel}</span>{' '}
          {rule.op === 'checked' ? 'is checked' : 'has a value'}
        </>
      ) : (
        emptyText
      )}
    </div>
    <div className="flex gap-1.5">
      <button
        type="button"
        onClick={onPick}
        className={cn(
          'flex-1 rounded-md border border-input bg-card px-2 py-1.5 text-xs font-medium text-foreground hover:bg-accent',
          isLinking && 'ring-2 ring-foreground/40 ring-offset-1 ring-offset-card',
        )}
      >
        {isLinking ? 'Click a field on the form…' : rule ? 'Change trigger' : 'Pick trigger field'}
      </button>
      {rule && (
        <button
          type="button"
          onClick={onClear}
          className="rounded-md px-2 py-1.5 text-xs font-medium text-destructive hover:bg-destructive/10"
        >
          Clear
        </button>
      )}
    </div>
  </div>
);

// A labeled settings section: uppercase muted header + a full-bleed top divider.
// `-mx-4` cancels the card's p-4 so the rule spans the full card width, then
// `px-4` re-pads the content back into alignment. Keeps the panel scannable.
const Section = ({ title, tone, children }) => (
  <div className="-mx-4 space-y-2.5 border-t border-border px-4 pt-3">
    <span
      className={cn(
        'block text-[11px] font-semibold uppercase tracking-wide',
        tone || 'text-muted-foreground',
      )}
    >
      {title}
    </span>
    {children}
  </div>
);

// Group settings — shared by the single-field panel and the multi-select panel.
// Naming and logic are split into separate sub-sections. `footer` is the context
// action (remove-this-field vs ungroup-all).
const GroupSettings = ({
  info,
  onRename,
  onToggleSingle,
  onSelectAll,
  triggerLabel,
  isLinking,
  onPickTrigger,
  onClearTrigger,
  onToggleAutoAdvance,
  onToggleRequireOne,
  onToggleRequireOneTyped,
  selectedFieldId,
  onSelectMember,
  onReorder,
  onAddField,
  isAddingField,
  footer,
}) => (
  <div className="space-y-4">
    <Section title="Name">
      <input
        value={info.name}
        onChange={(e) => onRename(e.target.value)}
        placeholder="Group name"
        className="h-8 w-full rounded-md border border-input bg-background px-2 text-sm text-foreground"
      />
      <button
        type="button"
        onClick={onSelectAll}
        className="text-[11px] font-medium text-primary hover:underline"
      >
        Select all {info.count} fields
      </button>
    </Section>

    {/* Fields — ordered; drag to reorder. The order drives downstream logic
        (e.g. signer auto-advance). Click a row to edit that field above. */}
    <Section title="Fields">
      <DragDropContext
        onDragEnd={(r) => {
          if (r.destination) onReorder(r.source.index, r.destination.index);
        }}
      >
        <Droppable droppableId="group-members">
          {(dp) => (
            <div ref={dp.innerRef} {...dp.droppableProps} className="space-y-1">
              {info.members.map((m, i) => {
                const meta = fieldTypeMeta(m.type);
                const Icon = meta.icon;
                const req = Boolean(m.required || m.requiredIf);
                const isSel = m.fieldId === selectedFieldId;
                return (
                  <Draggable key={m.fieldId} draggableId={m.fieldId} index={i}>
                    {(dr) => (
                      <div
                        ref={dr.innerRef}
                        {...dr.draggableProps}
                        className={cn(
                          'flex items-center gap-1.5 rounded-md border bg-card px-1.5 py-1.5 text-xs',
                          isSel ? 'border-primary ring-1 ring-primary/40' : 'border-input',
                        )}
                      >
                        <span
                          {...dr.dragHandleProps}
                          className="cursor-grab text-muted-foreground hover:text-foreground"
                          aria-label="Drag to reorder"
                        >
                          <GripVertical className="h-3.5 w-3.5" />
                        </span>
                        <button
                          type="button"
                          onClick={() => onSelectMember(m.fieldId)}
                          className="flex min-w-0 flex-1 items-center gap-1.5 text-left"
                        >
                          <Icon className={cn('h-3.5 w-3.5 shrink-0', meta.color)} />
                          <span className="truncate font-medium text-foreground">
                            {m.label || `Untitled ${meta.label.toLowerCase()}`}
                          </span>
                        </button>
                        {req ? <span className="shrink-0 text-destructive">*</span> : null}
                      </div>
                    )}
                  </Draggable>
                );
              })}
              {dp.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
      <button
        type="button"
        onClick={onAddField}
        className={cn(
          'flex w-full items-center justify-center gap-1.5 rounded-md border border-dashed border-input px-2 py-1.5 text-xs font-medium text-muted-foreground hover:bg-accent hover:text-foreground',
          isAddingField &&
            'text-foreground ring-2 ring-foreground/40 ring-offset-1 ring-offset-card',
        )}
      >
        <Plus className="h-3.5 w-3.5" />
        {isAddingField ? 'Click a field on the form…' : 'Add field to group'}
      </button>
    </Section>

    <Section title="Logic">
      <RulePicker
        summaryPrefix="Enabled when"
        emptyText="Always enabled"
        rule={info.enabledIf}
        triggerLabel={triggerLabel}
        isLinking={isLinking}
        onPick={onPickTrigger}
        onClear={onClearTrigger}
      />
      {info.checkableCount >= 2 && (
        <label className="flex items-center gap-2 text-sm text-foreground">
          <input
            type="checkbox"
            checked={info.single}
            onChange={(e) => onToggleSingle(e.target.checked)}
          />
          Single Selection
        </label>
      )}
      {info.checkableCount >= 1 && (
        <label className="flex items-center gap-2 text-sm text-foreground">
          <input
            type="checkbox"
            checked={info.requireOne}
            onChange={(e) => onToggleRequireOne(e.target.checked)}
          />
          Require a selection
        </label>
      )}
      {info.typedCount >= 2 && (
        <label className="flex items-start gap-2 text-sm text-foreground">
          <input
            type="checkbox"
            className="mt-1"
            checked={info.requireOneTyped}
            onChange={(e) => onToggleRequireOneTyped(e.target.checked)}
          />
          <span>
            Require one of these
            <span className="block text-[11px] text-muted-foreground">
              The signer fills at least one in full (an SSN or an EIN).
            </span>
          </span>
        </label>
      )}
      {info.advanceableCount >= 1 && (
        <label className="flex items-center gap-2 text-sm text-foreground">
          <input
            type="checkbox"
            checked={info.autoAdvance}
            onChange={(e) => onToggleAutoAdvance(e.target.checked)}
          />
          Auto-advance as fields fill
        </label>
      )}
    </Section>

    {footer}
  </div>
);

// Group members that can carry "require one of these" (mirrors the server).
const TYPED_REQUIRE_ONE = new Set(['text', 'long_text', 'number', 'date', 'digits']);

// Digit boxes settings (2026-10-15): a preset or a custom box count and
// runs ("3-2-4"), the gap between runs, letters allowed, sensitive. Resize the
// field over the form's printed boxes; the boxes split it evenly.
const DigitBoxSettings = ({ p, onChange }) => {
  const cells = digitCellsOf(p);
  const segments = digitSegmentsOf(p, cells);
  const runs = segments.join('-');
  const preset =
    DIGIT_PRESETS.find((x) => x.cells === cells && x.segments.join('-') === runs)?.key || 'custom';
  const [runsDraft, setRunsDraft] = useState(runs);
  useEffect(() => setRunsDraft(runs), [runs]);
  const inputCls =
    'mt-1 h-9 w-full rounded-md border border-input bg-background px-2 text-sm text-foreground';
  return (
    <div className="space-y-2">
      <label className="block text-xs font-medium text-muted-foreground">
        Layout
        <select
          value={preset}
          onChange={(e) => {
            const x = DIGIT_PRESETS.find((d) => d.key === e.target.value);
            if (x) onChange({ cells: x.cells, segments: x.segments, ...(x.sensitive ? { sensitive: true } : {}) });
          }}
          className={inputCls}
        >
          {DIGIT_PRESETS.map((x) => (
            <option key={x.key} value={x.key}>
              {x.label}
            </option>
          ))}
          <option value="custom">Custom</option>
        </select>
      </label>
      <div className="flex gap-2">
        <label className="block flex-1 text-xs font-medium text-muted-foreground">
          Boxes
          <input
            type="number"
            min={DIGIT_CELLS_MIN}
            max={DIGIT_CELLS_MAX}
            value={cells}
            onChange={(e) => {
              const n = Math.max(
                DIGIT_CELLS_MIN,
                Math.min(DIGIT_CELLS_MAX, parseInt(e.target.value, 10) || DIGIT_CELLS_MIN),
              );
              onChange({ cells: n, segments: [n] });
            }}
            className={inputCls}
          />
        </label>
        <label className="block flex-1 text-xs font-medium text-muted-foreground">
          Runs
          <input
            value={runsDraft}
            onChange={(e) => setRunsDraft(e.target.value)}
            onBlur={() => {
              const segs = runsDraft
                .split(/[^0-9]+/)
                .map((x) => parseInt(x, 10))
                .filter((x) => x > 0);
              const total = segs.reduce((a, b) => a + b, 0);
              if (segs.length && total >= DIGIT_CELLS_MIN && total <= DIGIT_CELLS_MAX) {
                onChange({ cells: total, segments: segs });
              } else {
                setRunsDraft(runs);
              }
            }}
            placeholder="3-2-4"
            className={inputCls}
          />
        </label>
      </div>
      {segments.length > 1 ? (
        <label className="block text-xs font-medium text-muted-foreground">
          Gap between runs
          <input
            type="range"
            min="0"
            max={DIGIT_GAP_MAX}
            step="0.05"
            value={Number(p.cellGap) || 0}
            onChange={(e) => onChange({ cellGap: Number(e.target.value) || undefined })}
            className="mt-2 w-full"
          />
        </label>
      ) : null}
      <label className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
        <input
          type="checkbox"
          checked={Boolean(p.allowLetters)}
          onChange={(e) => onChange({ allowLetters: e.target.checked || undefined })}
        />
        Allow letters (digits only when off)
      </label>
      <label className="flex items-start gap-2 text-xs font-medium text-muted-foreground">
        <input
          type="checkbox"
          className="mt-0.5"
          checked={Boolean(p.sensitive)}
          onChange={(e) => onChange({ sensitive: e.target.checked || undefined })}
        />
        <span>
          Sensitive
          <span className="mt-0.5 block font-normal text-muted-foreground/70">
            Do not store in DB (SSN, EIN)
          </span>
        </span>
      </label>
      <p className="mb-0 text-[11px] text-muted-foreground">
        Resize the field over the printed boxes; the boxes split it evenly. Required applies to the
        whole number.
      </p>
    </div>
  );
};

const FieldBox = ({ p, selected, showResize, lineCount, linkable, isTrigger, onFieldDown, onResizeStart }) => {
  const meta = fieldTypeMeta(p.type);
  const Icon = meta.icon;
  const round = p.type === 'radio' ? 'rounded-full' : 'rounded-sm';
  // Prepared regions (fee_chart) always show a dashed border: the box IS the
  // deliverable (the send flow renders the table into exactly this space), so
  // its footprint must stay visible even when unselected.
  const preparedRegion = p.type === 'fee_chart';
  const reqMark = requiredMark(p);
  return (
    <div
      onMouseDown={(e) => {
        if (e.button !== 0) return; // let middle-click bubble up to pan
        e.stopPropagation();
        onFieldDown(e, p);
      }}
      className={cn(
        // DocuSeal-style: translucent fill, no border at rest; the field's-color
        // border appears only when selected. border-2 border-transparent keeps
        // the box size stable so selecting doesn't shift it.
        'absolute flex cursor-move items-center justify-center border-2 border-transparent',
        round,
        // Digit boxes keep the gaps clear: only the boxes themselves are tinted.
        p.type === 'digits' ? 'bg-transparent' : meta.fill,
        preparedRegion && cn('border-dashed', meta.border),
        selected && meta.border,
        // While picking a trigger, eligible fields glow + are clickable.
        linkable && 'cursor-pointer ring-2 ring-primary/70 ring-offset-1 ring-offset-card',
        // The trigger of the currently-selected conditional field.
        isTrigger && !linkable && 'ring-2 ring-status-warning ring-offset-1 ring-offset-card',
      )}
      style={{
        left: `${p.x * 100}%`,
        top: `${p.y * 100}%`,
        width: `${p.w * 100}%`,
        height: `${p.h * 100}%`,
      }}
    >
      {/* A set static signature shows its image; everything else (and an unset
          static signature) shows the centered type icon. */}
      {p.type === 'static_signature' && p.staticValue ? (
        <img
          src={p.staticValue}
          alt="signature"
          className="pointer-events-none max-h-full max-w-full object-contain p-0.5"
        />
      ) : preparedRegion ? (
        <span className="pointer-events-none flex items-center gap-1.5">
          <Icon className={cn('h-4 w-4', meta.color)} />
          <span className={cn('text-[11px] font-semibold', meta.color)}>
            Fee Chart (prepared)
          </span>
        </span>
      ) : p.type === 'digits' ? (
        // Digit boxes (2026-10-15): the boxes themselves, so the field can be
        // lined up over the form's printed boxes.
        <span className="pointer-events-none absolute inset-0">
          {digitBoxLayout(p).boxes.map((b, i) => (
            // Each box tinted, a 2px outline and its number, so it reads
            // clearly against the form's printed boxes (Nic 2026-10-15).
            <span
              key={i}
              className="absolute inset-y-0 flex items-center justify-center rounded-[2px] border-2 border-teal-600 bg-teal-400/30 text-[9px] font-bold leading-none text-teal-800 dark:border-teal-300 dark:bg-teal-300/25 dark:text-teal-100"
              style={{ left: `${b.left * 100}%`, width: `${b.width * 100}%` }}
            >
              {i + 1}
            </span>
          ))}
        </span>
      ) : p.type === 'long_text' ? (
        // Long text shows its icon + the number of lines that fit at the current
        // box height / font size (updates live while resizing).
        <span className="pointer-events-none flex items-center gap-1">
          <Icon className={cn('h-4 w-4', meta.color)} />
          {lineCount != null ? (
            <span className={cn('text-[11px] font-semibold tabular-nums', meta.color)}>{lineCount}</span>
          ) : null}
        </span>
      ) : (
        <Icon className={cn('pointer-events-none max-h-8 min-h-[8px] w-auto', meta.color)} style={{ height: '55%' }} />
      )}
      {/* Required at a glance (Nic 2026-10-15): a red asterisk in the top
          right. Faded = only sometimes required (a "required when" rule, or
          one of a "require one" group). */}
      {reqMark ? (
        <span
          title={
            reqMark === 'always'
              ? 'Required'
              : reqMark === 'conditional'
                ? 'Required when its rule is met'
                : 'One of this group is required'
          }
          className={cn(
            'pointer-events-none absolute right-0.5 top-0 select-none text-[13px] font-bold leading-none text-red-600 dark:text-red-400',
            reqMark !== 'always' && 'opacity-50',
          )}
        >
          *
        </span>
      ) : null}
      {/* Resize handle only when this is the sole selected field, so it doesn't
          cover tiny unselected fields or appear on every multi-selected field. */}
      {showResize ? (
        <span
          onMouseDown={(e) => {
            if (e.button !== 0) return; // let middle-click bubble up to pan
            e.stopPropagation();
            onResizeStart(e, p);
          }}
          className="absolute -bottom-1.5 -right-1.5 h-2.5 w-2.5 cursor-se-resize rounded-sm border border-background bg-foreground"
        />
      ) : null}
    </div>
  );
};

// Inline-editable document name — click the name (✎) to rename, ✓ to confirm,
// Esc to cancel. Mirrors the lead email inline-edit pattern.
const EditableTitle = ({ name, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  const begin = () => {
    setDraft(name || '');
    setEditing(true);
  };
  const cancel = () => {
    setEditing(false);
    setDraft('');
  };
  const commit = async () => {
    const value = draft.trim();
    if (!value || saving) return;
    if (value === name) {
      cancel();
      return;
    }
    setSaving(true);
    try {
      await onSave(value);
      setEditing(false);
      setDraft('');
    } catch (err) {
      toast.error(err?.response?.data?.msg || 'Could not rename.');
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1.5">
        <input
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
            if (e.key === 'Escape') cancel();
          }}
          className="h-8 w-64 rounded-md border border-input bg-background px-2 text-base font-semibold text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
        <button
          type="button"
          onClick={commit}
          disabled={!draft.trim() || saving}
          aria-label="Save name"
          className="rounded-full p-1 text-muted-foreground transition-colors hover:bg-status-success/10 hover:text-status-success disabled:opacity-40"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
        </button>
        <button type="button" onClick={cancel} aria-label="Cancel" className="text-muted-foreground hover:text-foreground">
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <button type="button" onClick={begin} title="Rename" className="group flex min-w-0 items-center gap-1.5">
      <h2 className="m-0 truncate text-base font-semibold text-foreground">{name}</h2>
      <Pencil className="h-3.5 w-3.5 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
    </button>
  );
};

const DocumentTemplateBuilder = ({ documentId, onBack }) => {
  const canvasRef = useRef(null);
  const overlayRef = useRef(null);
  const dragRef = useRef(null);
  const pdfRef = useRef(null);
  const scrollRef = useRef(null);
  const panRef = useRef(null);
  // Scroll position to apply right after the next zoom re-render, so Ctrl+wheel
  // zooms toward the cursor rather than the top-left origin.
  const pendingScrollRef = useRef(null);
  // Holds a copied field for paste (Ctrl+V).
  const clipboardRef = useRef(null);
  // The documentId whose placements are already hydrated into canvas state.
  // Guards against re-hydration on template refetches (e.g. a rename invalidates
  // the template query), which would clobber unsaved placement work.
  const hydratedForRef = useRef(null);
  // Serialized placements as of the last hydration/save — the baseline for the
  // unsaved-changes (dirty) check.
  const savedSnapshotRef = useRef(null);

  const { data: template, isLoading } = useFetchTemplate(documentId);
  const { data: pdfMeta } = useFetchTemplatePdfUrl(documentId);
  const save = useUpdateTemplate();
  const rename = useUpdateTemplate();
  const handleRename = async (name) => {
    await rename.mutateAsync({ documentId, name });
    toast.success('Renamed.');
  };

  const [placements, setPlacements] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [marqueeRect, setMarqueeRect] = useState(null);
  const [page, setPage] = useState(1);
  const [numPages, setNumPages] = useState(1);
  const [zoom, setZoom] = useState(1);
  const [ready, setReady] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  // A fee chart is drawn in at send time from the client's rating, so the
  // Preview asks the server for the template with a sample one drawn in
  // (Nic 2026-10-25: a 40% client), using the placements as they are now.
  const [previewPdf, setPreviewPdf] = useState(null); // { url, sample }
  const [previewLoading, setPreviewLoading] = useState(false);
  const openPreview = async () => {
    if (!placements.some((p) => p.type === 'fee_chart')) {
      setPreviewOpen(true);
      return;
    }
    setPreviewLoading(true);
    try {
      setPreviewPdf(await fetchTemplatePreviewUrl(documentId, placements));
    } catch (err) {
      // Still preview, just without the sample chart.
      toast.error(err?.response?.data?.msg || "The fee chart sample couldn't be drawn.");
    } finally {
      setPreviewLoading(false);
      setPreviewOpen(true);
    }
  };
  const closePreview = () => {
    setPreviewOpen(false);
    setPreviewPdf((cur) => {
      if (cur?.url) URL.revokeObjectURL(cur.url);
      return null;
    });
  };
  const [panning, setPanning] = useState(false);
  // Current page height in PDF points — used to estimate how many lines a
  // long_text box fits. Defaults to US Letter until the PDF reports its size.
  const [pageHeightPts, setPageHeightPts] = useState(792);
  // A field type "armed" from the palette: the next click on the document drops
  // it there (click-to-place). null = not arming.
  const [armedType, setArmedType] = useState(null);
  const [sigModalOpen, setSigModalOpen] = useState(false);
  // Click-to-link a conditional trigger: { kind:'field'|'group', id }. While set,
  // clicking a field on the canvas picks it as that target's enable trigger.
  const [linking, setLinking] = useState(null);
  // Arm "add to group": holds a group id. While set, the next field clicked on the
  // canvas joins that group (at the end of its order).
  const [addingToGroup, setAddingToGroup] = useState(null);
  // Placements differ from the last hydrated/saved snapshot (unsaved work).
  const [dirty, setDirty] = useState(false);

  // Hydrate placements ONCE per document. The template query refetches after any
  // template mutation (rename, etc.) — re-hydrating then would reset the canvas
  // to server truth and destroy unsaved placements.
  useEffect(() => {
    if (!template?.fieldPlacements || hydratedForRef.current === documentId) return;
    const parsed = parsePlacements(template.fieldPlacements);
    hydratedForRef.current = documentId;
    savedSnapshotRef.current = JSON.stringify(parsed);
    setPlacements(parsed);
    setDirty(false);
  }, [template, documentId]);

  // Dirty check: compare placements to the snapshot only while still clean, so
  // high-frequency updates (drags) don't re-stringify once already dirty.
  useEffect(() => {
    if (dirty || savedSnapshotRef.current == null) return;
    if (JSON.stringify(placements) !== savedSnapshotRef.current) setDirty(true);
  }, [placements, dirty]);

  // Warn before the tab closes / navigates away while there's unsaved work.
  useEffect(() => {
    if (!dirty) return undefined;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = ''; // required by Chrome to show the prompt
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  // Load + render the PDF page at the current zoom. The overlay is %-based, so
  // re-rendering the canvas wider/narrower scales the fields with it (no CSS
  // transform needed). `ready` gates the first paint so the page area doesn't
  // collapse + cram the fields before pdf.js finishes.
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!pdfMeta?.url || !canvasRef.current) return;
      try {
        if (!pdfRef.current) {
          const { pdf, numPages: n } = await loadPdfDocument(pdfMeta.url);
          if (cancelled) return;
          pdfRef.current = pdf;
          setNumPages(n || 1);
        }
        const res = await renderPageToCanvas(pdfRef.current, page, canvasRef.current, RENDER_WIDTH * zoom);
        // height(px) / scale(px-per-point) = page height in PDF points.
        if (!cancelled && res?.scale) setPageHeightPts(res.height / res.scale);
        // Now that the canvas is resized, apply any cursor-anchored scroll target.
        if (!cancelled && pendingScrollRef.current && scrollRef.current) {
          scrollRef.current.scrollLeft = pendingScrollRef.current.left;
          scrollRef.current.scrollTop = pendingScrollRef.current.top;
          pendingScrollRef.current = null;
        }
      } catch (err) {
        const c = canvasRef.current;
        if (c) {
          c.width = RENDER_WIDTH * zoom;
          c.height = Math.round(RENDER_WIDTH * zoom * (11 / 8.5));
          c.style.width = `${c.width}px`;
          c.style.height = `${c.height}px`;
          const ctx = c.getContext('2d');
          ctx.fillStyle = '#fff';
          ctx.fillRect(0, 0, c.width, c.height);
          ctx.fillStyle = '#94a3b8';
          ctx.font = '14px sans-serif';
          ctx.fillText('PDF preview unavailable — placement still works', 20, 30);
        }
      } finally {
        if (!cancelled) setReady(true);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
    // isLoading: the canvas only mounts once the template has loaded. When the
    // PDF url arrived first, this ran with no canvas and gave up, and nothing
    // re-ran it: the page sat on the spinner until a refresh won the race.
  }, [pdfMeta, page, zoom, isLoading]);

  // The single-field properties editor only applies when exactly one is selected.
  const selected = useMemo(
    () => (selectedIds.length === 1 ? placements.find((p) => p.fieldId === selectedIds[0]) || null : null),
    [placements, selectedIds],
  );
  const selectedPlacements = useMemo(
    () => placements.filter((p) => selectedIds.includes(p.fieldId)),
    [placements, selectedIds],
  );
  // Every distinct group in the document, for the Groups list.
  const groupList = useMemo(() => {
    const map = new Map();
    placements.forEach((p) => {
      if (!p.group) return;
      if (!map.has(p.group))
        map.set(p.group, { gid: p.group, name: '', count: 0, single: false, requireOne: false });
      const g = map.get(p.group);
      g.count += 1;
      if (p.groupName) g.name = p.groupName;
      if (p.groupSingle) g.single = true;
      if (p.groupRequired) g.requireOne = true;
    });
    return [...map.values()].map((g) => ({ ...g, name: g.name || 'Group' }));
  }, [placements]);

  const update = (fieldId, patch) =>
    setPlacements((prev) =>
      prev.map((p) => (p.fieldId === fieldId ? { ...p, ...patch } : p)),
    );

  // ---- Align + distribute the current selection (operates on its bounding box) ----
  const applyToSelected = (fn) =>
    setPlacements((prev) =>
      prev.map((p) => (selectedIds.includes(p.fieldId) ? { ...p, ...fn(p) } : p)),
    );

  // Logical grouping (any fields). A group ties fields together as a unit for
  // rules; single-choice flags a (checkable) group as mutually exclusive.
  // Grouping does NOT change selection/drag — it's purely logical.
  const applyToGroup = (gid, fn) =>
    setPlacements((prev) => prev.map((p) => (p.group === gid ? { ...p, ...fn(p) } : p)));
  const groupInfo = (gid) => {
    const members = groupMembersOrdered(placements, gid);
    // Only text/number fields can auto-advance (they have a max-char "full" event).
    const advanceable = members.filter((p) => p.type === 'text' || p.type === 'number');
    const checkable = members.filter((p) => p.type === 'checkbox' || p.type === 'radio');
    // Typed members can carry "require one of these" (SSN or EIN, 2026-10-15).
    const typed = members.filter((p) => TYPED_REQUIRE_ONE.has(p.type));
    return {
      members,
      name: (members.find((p) => p.groupName) || {}).groupName || '',
      count: members.length,
      checkableCount: checkable.length,
      single: members.length > 0 && members.every((p) => p.groupSingle),
      // "Require a selection": ≥1 checkable member must be selected (group-wide).
      requireOne: checkable.length > 0 && checkable.every((p) => p.groupRequired),
      typedCount: typed.length,
      requireOneTyped: typed.length > 0 && typed.every((p) => p.groupRequired),
      advanceableCount: advanceable.length,
      autoAdvance: advanceable.length > 0 && advanceable.every((p) => p.autoAdvance),
      enabledIf: (members.find((p) => p.enabledIf) || {}).enabledIf || null,
    };
  };
  // Human label for a trigger rule (the picked field's label or its type).
  const triggerLabelFor = (rule) => {
    if (!rule || !rule.fieldId) return '';
    const t = placements.find((p) => p.fieldId === rule.fieldId);
    return t ? t.label || fieldTypeMeta(t.type).label : 'a field';
  };
  // Field ids that can't be a trigger for the current link target (itself / its
  // group members).
  const linkExcluded =
    linking && linking.kind === 'group'
      ? groupInfo(linking.id).members.map((m) => m.fieldId)
      : linking
        ? [linking.id]
        : [];
  // Apply a click-to-link trigger pick to the current link target (enabledIf or
  // requiredIf, per linking.rule).
  const applyTrigger = (triggerField) => {
    if (!linking || linkExcluded.includes(triggerField.fieldId)) return;
    const op =
      triggerField.type === 'checkbox' || triggerField.type === 'radio' ? 'checked' : 'filled';
    const ruleObj = { fieldId: triggerField.fieldId, op };
    const key = linking.rule || 'enabledIf';
    if (linking.kind === 'group') applyToGroup(linking.id, () => ({ [key]: ruleObj }));
    else update(linking.id, { [key]: ruleObj });
    setLinking(null);
  };
  const groupSelected = () => {
    const used = new Set(placements.map((p) => p.groupName).filter(Boolean));
    let n = 1;
    while (used.has(`Group ${n}`)) n += 1;
    const gid = `grp-${newFieldId()}`;
    // Seed groupOrder from the selection order so the member list has a stable order.
    const orderMap = new Map(selectedIds.map((id, i) => [id, i]));
    setPlacements((prev) =>
      prev.map((p) =>
        selectedIds.includes(p.fieldId)
          ? { ...p, group: gid, groupName: `Group ${n}`, groupOrder: orderMap.get(p.fieldId) }
          : p,
      ),
    );
    toast.success(`Grouped ${selectedIds.length} fields into “Group ${n}”`);
  };
  // Reorder a group's members (drag in the group list) → rewrite groupOrder.
  const reorderGroup = (gid, fromIndex, toIndex) => {
    const members = groupMembersOrdered(placements, gid);
    if (
      fromIndex == null ||
      toIndex == null ||
      fromIndex < 0 ||
      toIndex < 0 ||
      fromIndex >= members.length ||
      toIndex >= members.length
    )
      return;
    const next = [...members];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    const orderMap = new Map(next.map((m, i) => [m.fieldId, i]));
    setPlacements((prev) =>
      prev.map((p) => (p.group === gid ? { ...p, groupOrder: orderMap.get(p.fieldId) } : p)),
    );
  };
  // Add a field to an existing group (at the end of its order).
  const addToGroup = (gid, fieldId) => {
    if (!gid || !fieldId) return;
    const members = groupMembersOrdered(placements, gid);
    if (members.some((m) => m.fieldId === fieldId)) return;
    const name = (members.find((m) => m.groupName) || {}).groupName;
    const single = members.length > 0 && members.every((m) => m.groupSingle) ? true : undefined;
    const maxOrder = members.reduce((mx, p) => Math.max(mx, p.groupOrder ?? -1), -1);
    update(fieldId, {
      group: gid,
      groupName: name,
      groupSingle: single,
      groupOrder: maxOrder + 1,
    });
  };
  const renameGroup = (gid, name) => applyToGroup(gid, () => ({ groupName: name || undefined }));
  const setGroupSingle = (gid, on) => applyToGroup(gid, () => ({ groupSingle: on || undefined }));
  const ungroupGroup = (gid) =>
    applyToGroup(gid, () => ({
      group: undefined,
      groupName: undefined,
      groupSingle: undefined,
      groupOrder: undefined,
    }));
  const removeFromGroup = (fieldId) =>
    update(fieldId, {
      group: undefined,
      groupName: undefined,
      groupSingle: undefined,
      groupOrder: undefined,
    });
  const selectGroup = (gid) => {
    const members = placements.filter((p) => p.group === gid);
    if (!members.length) return;
    setSelectedIds(members.map((p) => p.fieldId));
    if (!members.some((m) => (m.page || 1) === page)) setPage(members[0].page || 1);
  };
  // The common group of the whole selection (null if mixed/none).
  const selSharedGroup =
    selectedPlacements.length > 0 &&
    new Set(selectedPlacements.map((p) => p.group || null)).size === 1
      ? selectedPlacements[0].group || null
      : null;
  // Trigger field(s) of whatever is selected, so we can ring them on the canvas.
  const activeTriggerIds = new Set(
    [
      selected && selected.enabledIf && selected.enabledIf.fieldId,
      selected && selected.requiredIf && selected.requiredIf.fieldId,
      selSharedGroup ? groupInfo(selSharedGroup).enabledIf?.fieldId : null,
    ].filter(Boolean),
  );

  const align = (kind) => {
    const sel = selectedPlacements;
    if (sel.length < 2) return;
    const minX = Math.min(...sel.map((p) => p.x));
    const maxR = Math.max(...sel.map((p) => p.x + p.w));
    const minY = Math.min(...sel.map((p) => p.y));
    const maxB = Math.max(...sel.map((p) => p.y + p.h));
    applyToSelected((p) => {
      switch (kind) {
        case 'left':
          return { x: minX };
        case 'right':
          return { x: maxR - p.w };
        case 'centerH':
          return { x: (minX + maxR) / 2 - p.w / 2 };
        case 'top':
          return { y: minY };
        case 'bottom':
          return { y: maxB - p.h };
        case 'middleV':
          return { y: (minY + maxB) / 2 - p.h / 2 };
        default:
          return {};
      }
    });
  };

  // Space the selection evenly (equal gaps), keeping the two end fields put.
  const distribute = (axis) => {
    const sel = [...selectedPlacements];
    if (sel.length < 3) return;
    const sizeKey = axis === 'h' ? 'w' : 'h';
    const posKey = axis === 'h' ? 'x' : 'y';
    sel.sort((a, b) => a[posKey] - b[posKey]);
    const start = sel[0][posKey];
    const end = sel[sel.length - 1][posKey] + sel[sel.length - 1][sizeKey];
    const totalSize = sel.reduce((s, p) => s + p[sizeKey], 0);
    const gap = (end - start - totalSize) / (sel.length - 1);
    const byId = {};
    let cursor = start;
    sel.forEach((p) => {
      byId[p.fieldId] = cursor;
      cursor += p[sizeKey] + gap;
    });
    setPlacements((prev) =>
      prev.map((p) => (byId[p.fieldId] !== undefined ? { ...p, [posKey]: byId[p.fieldId] } : p)),
    );
  };

  // Add a field. `pos` (normalized 0..1) places it centered on the click point
  // (click-to-place); without it the field drops at a default corner.
  const addField = (type, pos) => {
    const size = defaultSizeFor(type);
    const p = {
      fieldId: newFieldId(),
      page,
      x: pos ? clamp01(pos.x - size.w / 2) : 0.1,
      y: pos ? clamp01(pos.y - size.h / 2) : 0.1,
      type,
      label: '',
      fontSize: 11,
      align: 'left',
      required: false,
      // New text/number fields default to limit-overflow (fit to box). Harmless on
      // other types — the server strips it for non-text/number on save.
      ...(type === 'text' || type === 'number' ? { limitToBox: true } : {}),
      ...size,
    };
    if (type === 'static_text') p.staticValue = '';
    // A new digit field starts as an SSN, so Sensitive starts on too.
    if (type === 'digits') Object.assign(p, { cells: 9, segments: [3, 2, 4], cellGap: 0.4, sensitive: true });
    setPlacements((prev) => [...prev, p]);
    setSelectedIds([p.fieldId]);
  };

  // Mouse down on empty canvas (a field stops propagation): place an armed field,
  // otherwise start a marquee (rubber-band) selection. Left button only (middle
  // = pan). A click with no drag clears the selection.
  const onOverlayMouseDown = (e) => {
    if (e.button !== 0) return;
    if (addingToGroup) {
      setAddingToGroup(null); // clicking empty canvas cancels add-to-group
      return;
    }
    if (linking) {
      setLinking(null); // clicking empty canvas cancels trigger-picking
      return;
    }
    const rect = overlayRef.current.getBoundingClientRect();
    if (armedType) {
      addField(armedType, {
        x: (e.clientX - rect.left) / rect.width,
        y: (e.clientY - rect.top) / rect.height,
      });
      setArmedType(null);
      return;
    }
    const startX = (e.clientX - rect.left) / rect.width;
    const startY = (e.clientY - rect.top) / rect.height;
    const additive = e.shiftKey || e.ctrlKey || e.metaKey;
    const baseSel = selectedIds;
    const pagePl = placements.filter((p) => (p.page || 1) === page);
    let moved = false;
    const onMove = (ev) => {
      const x = clamp01((ev.clientX - rect.left) / rect.width);
      const y = clamp01((ev.clientY - rect.top) / rect.height);
      if (Math.abs(x - startX) > 0.004 || Math.abs(y - startY) > 0.004) moved = true;
      const r = {
        left: Math.min(startX, x),
        top: Math.min(startY, y),
        right: Math.max(startX, x),
        bottom: Math.max(startY, y),
      };
      setMarqueeRect({ left: r.left, top: r.top, width: r.right - r.left, height: r.bottom - r.top });
      const hits = pagePl
        .filter((p) => !(p.x > r.right || p.x + p.w < r.left || p.y > r.bottom || p.y + p.h < r.top))
        .map((p) => p.fieldId);
      setSelectedIds(additive ? Array.from(new Set([...baseSel, ...hits])) : hits);
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      if (!moved && !additive) setSelectedIds([]);
      setMarqueeRect(null);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // Middle-mouse drag pans the PDF scroll area (like a hand tool).
  const onScrollMouseDown = (e) => {
    if (e.button !== 1) return;
    e.preventDefault(); // suppress the browser's middle-click autoscroll
    const el = scrollRef.current;
    if (!el) return;
    panRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      scrollLeft: el.scrollLeft,
      scrollTop: el.scrollTop,
    };
    setPanning(true);
  };

  const removeSelected = () => {
    if (!selectedIds.length) return;
    const n = selectedIds.length;
    setPlacements((prev) => prev.filter((p) => !selectedIds.includes(p.fieldId)));
    setSelectedIds([]);
    toast.success(`Deleted ${n} field${n > 1 ? 's' : ''}.`);
  };

  // Mouse down on a field: shift/ctrl-click toggles it in the selection;
  // a plain click selects just it (unless it's already part of a multi-selection,
  // in which case the whole group is kept so it can be dragged together).
  const onFieldMouseDown = (e, p) => {
    if (addingToGroup) {
      addToGroup(addingToGroup, p.fieldId);
      setAddingToGroup(null);
      return;
    }
    if (linking) {
      applyTrigger(p);
      return;
    }
    if (e.shiftKey || e.ctrlKey || e.metaKey) {
      setSelectedIds((prev) =>
        prev.includes(p.fieldId) ? prev.filter((id) => id !== p.fieldId) : [...prev, p.fieldId],
      );
      return;
    }
    const ids = selectedIds.includes(p.fieldId) ? selectedIds : [p.fieldId];
    if (!selectedIds.includes(p.fieldId)) setSelectedIds(ids);
    onDragStart(e, ids);
  };

  // --- Drag + resize (pointer math against the overlay rect, which already
  // reflects the current zoom since it's the rendered canvas size) ---
  const onDragStart = (e, ids) => {
    dragRef.current = {
      mode: 'move',
      startX: e.clientX,
      startY: e.clientY,
      rect: overlayRef.current.getBoundingClientRect(),
      origs: placements
        .filter((p) => ids.includes(p.fieldId))
        .map((p) => ({ fieldId: p.fieldId, origX: p.x, origY: p.y })),
    };
  };
  const onResizeStart = (e, p) => {
    dragRef.current = {
      mode: 'resize',
      fieldId: p.fieldId,
      type: p.type,
      startX: e.clientX,
      startY: e.clientY,
      origW: p.w,
      origH: p.h,
      rect: overlayRef.current.getBoundingClientRect(),
    };
  };

  const onMouseMove = useCallback((e) => {
    const d = dragRef.current;
    if (!d) return;
    const dx = (e.clientX - d.startX) / d.rect.width;
    const dy = (e.clientY - d.startY) / d.rect.height;
    if (d.mode === 'move') {
      // Move every field in the selection by the same delta.
      setPlacements((prev) =>
        prev.map((p) => {
          const o = d.origs.find((g) => g.fieldId === p.fieldId);
          if (!o) return p;
          return {
            ...p,
            x: Math.min(0.99, Math.max(0, o.origX + dx)),
            y: Math.min(0.99, Math.max(0, o.origY + dy)),
          };
        }),
      );
    } else {
      const w = Math.min(1, Math.max(0.012, d.origW + dx));
      let h = Math.min(1, Math.max(0.01, d.origH + dy));
      // Checkboxes + radios keep a square aspect (equal pixels) as they resize.
      if (d.type === 'checkbox' || d.type === 'radio') h = (w * d.rect.width) / d.rect.height;
      update(d.fieldId, { w, h });
    }
  }, []);

  const onMouseUp = useCallback(() => {
    dragRef.current = null;
  }, []);

  useEffect(() => {
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [onMouseMove, onMouseUp]);

  // Middle-mouse panning: translate the scroll position by the drag delta.
  useEffect(() => {
    if (!panning) return undefined;
    const onMove = (e) => {
      const el = scrollRef.current;
      const p = panRef.current;
      if (!el || !p) return;
      el.scrollLeft = p.scrollLeft - (e.clientX - p.startX);
      el.scrollTop = p.scrollTop - (e.clientY - p.startY);
    };
    const onUp = () => {
      setPanning(false);
      panRef.current = null;
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [panning]);

  // Arrow-key nudge the selected field (Shift = larger step). Ignored while a
  // text input/select in the properties panel has focus.
  useEffect(() => {
    if (!selectedIds.length) return undefined;
    const ARROWS = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];
    const onKey = (e) => {
      if (!ARROWS.includes(e.key)) return;
      const tag = (document.activeElement?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'select' || tag === 'textarea') return;
      e.preventDefault();
      // Fine nudge by default; Shift = a bigger step. Moves the whole selection.
      const step = e.shiftKey ? 0.005 : 0.0007;
      setPlacements((prev) =>
        prev.map((p) => {
          if (!selectedIds.includes(p.fieldId)) return p;
          let { x, y } = p;
          if (e.key === 'ArrowLeft') x -= step;
          if (e.key === 'ArrowRight') x += step;
          if (e.key === 'ArrowUp') y -= step;
          if (e.key === 'ArrowDown') y += step;
          return {
            ...p,
            x: Math.min(0.99, Math.max(0, x)),
            y: Math.min(0.99, Math.max(0, y)),
          };
        }),
      );
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedIds]);

  // Esc: cancel an armed mode (add-to-group / trigger-linking / click-to-place),
  // otherwise deselect.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (addingToGroup) setAddingToGroup(null);
      else if (linking) setLinking(null);
      else if (armedType) setArmedType(null);
      else setSelectedIds([]);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [armedType, linking, addingToGroup]);

  // Copy (Ctrl/Cmd+C) / paste (Ctrl/Cmd+V) / duplicate (Ctrl/Cmd+D) the selected
  // field. Ignored while typing in a property input so it doesn't hijack normal
  // text copy/paste. Pasted/duplicated fields land slightly offset on the
  // current page (carrying every property, incl. a static signature image).
  useEffect(() => {
    const onKey = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      const tag = (document.activeElement?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'select' || tag === 'textarea') return;
      const key = e.key.toLowerCase();
      // Paste a set of fields, offset onto the current page, preserving their
      // relative layout, and select the new copies.
      const pasteFields = (arr) => {
        const news = arr.map((src) => ({
          ...src,
          fieldId: newFieldId(),
          page,
          x: Math.min(0.99, (src.x || 0) + 0.02),
          y: Math.min(0.99, (src.y || 0) + 0.02),
        }));
        setPlacements((prev) => [...prev, ...news]);
        setSelectedIds(news.map((n) => n.fieldId));
      };
      if (key === 'c' && selectedPlacements.length) {
        clipboardRef.current = selectedPlacements.map((p) => ({ ...p }));
        e.preventDefault();
      } else if (key === 'v' && clipboardRef.current?.length) {
        e.preventDefault();
        pasteFields(clipboardRef.current);
      } else if (key === 'd' && selectedPlacements.length) {
        e.preventDefault();
        pasteFields(selectedPlacements);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedPlacements, page]);

  // Delete / Backspace removes the selected field(s) (unless typing in a field).
  useEffect(() => {
    if (!selectedIds.length) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Delete' && e.key !== 'Backspace') return;
      const tag = (document.activeElement?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'select' || tag === 'textarea') return;
      e.preventDefault();
      removeSelected();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedIds]); // eslint-disable-line react-hooks/exhaustive-deps

  // Ctrl + mouse wheel zooms the PDF (toward the cursor) instead of the browser
  // page. The listener must be non-passive to preventDefault, so it's attached
  // natively. Re-runs on zoom so the handler closes over the current zoom.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      if (!e.ctrlKey) return;
      e.preventDefault();
      const dir = e.deltaY < 0 ? 1 : -1;
      const next = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, +(zoom + dir * ZOOM_STEP).toFixed(2)));
      if (next === zoom) return;
      // Keep the point under the cursor fixed: scale its content-space position
      // by the zoom factor, then subtract the cursor offset. Applied after the
      // canvas re-renders (see the render effect).
      const factor = next / zoom;
      const rect = el.getBoundingClientRect();
      const offsetX = e.clientX - rect.left;
      const offsetY = e.clientY - rect.top;
      pendingScrollRef.current = {
        left: (el.scrollLeft + offsetX) * factor - offsetX,
        top: (el.scrollTop + offsetY) * factor - offsetY,
      };
      setZoom(next);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
    // Re-run once the scroll element exists (isLoading guard) and on zoom change.
  }, [isLoading, zoom]);

  const handleSave = async () => {
    const unsigned = placements.filter(
      (p) => p.type === 'static_signature' && !p.staticValue,
    ).length;
    try {
      await save.mutateAsync({ documentId, fieldPlacements: placements });
      savedSnapshotRef.current = JSON.stringify(placements);
      setDirty(false);
      toast.success('Template saved.');
      if (unsigned > 0) {
        toast.warn(
          `${unsigned} static signature field${unsigned > 1 ? 's are' : ' is'} placed but not signed.`,
        );
      }
    } catch (e) {
      toast.error(e?.response?.data?.msg || 'Save failed.');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  const pagePlacements = placements.filter((p) => (p.page || 1) === page);

  return (
    <div>
      <div className="mb-4 flex items-center gap-3">
        <button
          type="button"
          onClick={() => {
            // eslint-disable-next-line no-alert
            if (dirty && !window.confirm('You have unsaved field changes — leave anyway?'))
              return;
            onBack();
          }}
          className="inline-flex items-center gap-1.5 rounded-md border border-input bg-card px-3 py-1.5 text-sm font-medium text-foreground hover:bg-accent"
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <EditableTitle name={template?.name} onSave={handleRename} />
        {/* Reserved center space for the click-to-place hint, so showing/hiding
            it never reflows the canvas below. */}
        <div className="flex flex-1 justify-center">
          {armedType ? (
            <div className="inline-flex items-center gap-2 rounded-full bg-foreground/90 px-3 py-1.5 text-xs font-medium text-background">
              <MousePointerClick className="h-3.5 w-3.5" />
              Click on the document to place the {fieldTypeMeta(armedType).label}
              <button
                type="button"
                onClick={() => setArmedType(null)}
                className="ml-1 inline-flex items-center gap-1 rounded px-1.5 py-0.5 underline-offset-2 hover:underline"
              >
                <X className="h-3 w-3" /> Esc to cancel
              </button>
            </div>
          ) : linking ? (
            <div className="inline-flex items-center gap-2 rounded-full bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground">
              <MousePointerClick className="h-3.5 w-3.5" />
              {linking.rule === 'requiredIf'
                ? 'Click the field that should make this one required'
                : 'Click the field that should turn this on'}
              <button
                type="button"
                onClick={() => setLinking(null)}
                className="ml-1 inline-flex items-center gap-1 rounded px-1.5 py-0.5 underline-offset-2 hover:underline"
              >
                <X className="h-3 w-3" /> Esc to cancel
              </button>
            </div>
          ) : addingToGroup ? (
            <div className="inline-flex items-center gap-2 rounded-full bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground">
              <MousePointerClick className="h-3.5 w-3.5" />
              Click the field to add to “{groupInfo(addingToGroup).name || 'the group'}”
              <button
                type="button"
                onClick={() => setAddingToGroup(null)}
                className="ml-1 inline-flex items-center gap-1 rounded px-1.5 py-0.5 underline-offset-2 hover:underline"
              >
                <X className="h-3 w-3" /> Esc to cancel
              </button>
            </div>
          ) : null}
        </div>
        {dirty && (
          <span className="whitespace-nowrap text-xs font-medium text-muted-foreground">
            Unsaved changes
          </span>
        )}
        <button
          type="button"
          disabled={previewLoading}
          onClick={openPreview}
          className="inline-flex items-center gap-1.5 rounded-md border border-input bg-card px-4 py-2 text-sm font-medium text-foreground hover:bg-accent disabled:opacity-60"
        >
          {previewLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />} Preview
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={save.isLoading}
          className="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
        >
          {save.isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save
        </button>
      </div>

      {previewOpen && (
        <SignDocument
          previewMode
          previewDoc={{
            name: template?.name,
            description: previewPdf?.sample
              ? `Preview: the fee chart shows a sample ${previewPdf.sample.rating}% client (single, no kids). Each client gets their own.`
              : template?.description,
            pageCount: numPages,
            pdfUrl: previewPdf?.url || pdfMeta?.url,
            fieldPlacements: placements,
          }}
          onClosePreview={closePreview}
        />
      )}

      {sigModalOpen && selected?.type === 'static_signature' && (
        <SignatureModal
          allowUpload
          title="Set static signature"
          initialValue={selected.staticValue || null}
          onClose={() => setSigModalOpen(false)}
          onApply={(dataUrl) => {
            update(selected.fieldId, { staticValue: dataUrl });
            setSigModalOpen(false);
          }}
        />
      )}

      <div className="flex items-start gap-4">
        {/* Field palette */}
        <div className="sticky top-4 w-44 shrink-0 space-y-4">
          {FIELD_TYPE_GROUPS.map((group) => (
            <div key={group.title} className="space-y-2">
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {group.title}
              </div>
              {group.options.map((opt) => {
                const meta = fieldTypeMeta(opt.type);
                const Icon = meta.icon;
                const isArmed = armedType === opt.type;
                return (
                  <button
                    key={opt.type}
                    type="button"
                    onClick={() => setArmedType((prev) => (prev === opt.type ? null : opt.type))}
                    className={cn(
                      'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-xs font-medium hover:opacity-80',
                      meta.fill,
                      meta.color,
                      isArmed && 'ring-2 ring-foreground/40 ring-offset-1 ring-offset-card',
                    )}
                  >
                    <Icon className="h-3.5 w-3.5" /> {opt.label}
                  </button>
                );
              })}
            </div>
          ))}
          <p className="pt-1 text-[11px] leading-snug text-muted-foreground">
            Click a field, then click the document to place it.
          </p>
        </div>

        {/* Canvas + overlay */}
        <div className="min-w-0 flex-1">
          <div className="mb-2 flex min-h-9 items-center justify-center gap-4 text-sm">
            {numPages > 1 && (
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="rounded-md border border-input bg-card p-1 disabled:opacity-40"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="text-muted-foreground">
                  Page {page} of {numPages}
                </span>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(numPages, p + 1))}
                  disabled={page >= numPages}
                  className="rounded-md border border-input bg-card p-1 disabled:opacity-40"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            )}
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setZoom((z) => Math.max(ZOOM_MIN, +(z - ZOOM_STEP).toFixed(2)))}
                disabled={zoom <= ZOOM_MIN}
                className="rounded-md border border-input bg-card p-1 disabled:opacity-40"
                aria-label="Zoom out"
              >
                <ZoomOut className="h-4 w-4" />
              </button>
              <span className="w-12 text-center text-xs text-muted-foreground">
                {Math.round(zoom * 100)}%
              </span>
              <button
                type="button"
                onClick={() => setZoom((z) => Math.min(ZOOM_MAX, +(z + ZOOM_STEP).toFixed(2)))}
                disabled={zoom >= ZOOM_MAX}
                className="rounded-md border border-input bg-card p-1 disabled:opacity-40"
                aria-label="Zoom in"
              >
                <ZoomIn className="h-4 w-4" />
              </button>
            </div>

            {/* Align + distribute the selection (2+ fields). Lives here, by the
                zoom controls, rather than crowding the right panel. */}
            {selectedIds.length >= 2 && (
              <div className="flex items-center gap-0.5 rounded-md border border-input bg-card p-0.5">
                <MiniBtn onClick={() => align('left')} title="Align left">
                  <AlignStartVertical className="h-4 w-4" />
                </MiniBtn>
                <MiniBtn onClick={() => align('centerH')} title="Center horizontally">
                  <AlignCenterVertical className="h-4 w-4" />
                </MiniBtn>
                <MiniBtn onClick={() => align('right')} title="Align right">
                  <AlignEndVertical className="h-4 w-4" />
                </MiniBtn>
                <MiniBtn onClick={() => align('top')} title="Align top">
                  <AlignStartHorizontal className="h-4 w-4" />
                </MiniBtn>
                <MiniBtn onClick={() => align('middleV')} title="Center vertically">
                  <AlignCenterHorizontal className="h-4 w-4" />
                </MiniBtn>
                <MiniBtn onClick={() => align('bottom')} title="Align bottom">
                  <AlignEndHorizontal className="h-4 w-4" />
                </MiniBtn>
                <span className="mx-0.5 h-4 w-px bg-border" />
                <MiniBtn
                  onClick={() => distribute('h')}
                  disabled={selectedIds.length < 3}
                  title="Distribute horizontally"
                >
                  <AlignHorizontalDistributeCenter className="h-4 w-4" />
                </MiniBtn>
                <MiniBtn
                  onClick={() => distribute('v')}
                  disabled={selectedIds.length < 3}
                  title="Distribute vertically"
                >
                  <AlignVerticalDistributeCenter className="h-4 w-4" />
                </MiniBtn>
              </div>
            )}
          </div>

          <div
            ref={scrollRef}
            onMouseDown={onScrollMouseDown}
            className={cn(
              // Fixed height (not min/max) so the container never resizes with
              // zoom — the PDF just scrolls inside it and the page/footer stay put.
              'h-[calc(100vh-220px)] min-h-[440px] overflow-auto rounded-lg border border-border p-4',
              // Premium dot-grid canvas (Figma-style): a radial-gradient dot
              // pattern over a flat backdrop, with a faint light variant for dark.
              'bg-white bg-[radial-gradient(#00000026_1.2px,transparent_1.2px)] bg-[length:18px_18px]',
              'dark:bg-surface-page dark:bg-[radial-gradient(#ffffff1f_1.2px,transparent_1.2px)]',
              // Clean overlay scrollbar (both axes): no arrow buttons, transparent
              // track, floating rounded thumb — matches the settings panel.
              '[&::-webkit-scrollbar]:h-2 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-button]:hidden [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-muted-foreground/50 hover:[&::-webkit-scrollbar-thumb]:bg-muted-foreground/70',
              panning && 'cursor-grabbing select-none',
            )}
          >
            {/* min-w-min lets the row grow to the (zoomed) PDF so it scrolls
                INSIDE this container and stays reachable, while justify-center
                centers it when it's narrower than the container. */}
            <div className="flex min-w-min justify-center">
              <div
                ref={overlayRef}
                className={cn(
                  'relative inline-block self-start bg-white shadow-2xl ring-1 ring-black/5',
                  armedType && 'cursor-crosshair',
                )}
              style={!ready ? { width: RENDER_WIDTH, height: PLACEHOLDER_H } : undefined}
              onMouseDown={onOverlayMouseDown}
            >
              <canvas ref={canvasRef} className="block" />
              {!ready ? (
                <div className="absolute inset-0 flex items-center justify-center bg-secondary/50 dark:bg-surface-elevated">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <>
                  {pagePlacements.map((p) => (
                    <FieldBox
                      key={p.fieldId}
                      p={p}
                      selected={selectedIds.includes(p.fieldId)}
                      showResize={selectedIds.length === 1 && selectedIds[0] === p.fieldId}
                      lineCount={p.type === 'long_text' ? linesForBox(p.h, pageHeightPts, p.fontSize) : null}
                      linkable={Boolean(linking) && !linkExcluded.includes(p.fieldId)}
                      isTrigger={activeTriggerIds.has(p.fieldId)}
                      onFieldDown={onFieldMouseDown}
                      onResizeStart={onResizeStart}
                    />
                  ))}
                  {marqueeRect && (
                    <div
                      className="pointer-events-none absolute z-10 rounded-sm border border-primary bg-primary/10"
                      style={{
                        left: `${marqueeRect.left * 100}%`,
                        top: `${marqueeRect.top * 100}%`,
                        width: `${marqueeRect.width * 100}%`,
                        height: `${marqueeRect.height * 100}%`,
                      }}
                    />
                  )}
                </>
              )}
              </div>
            </div>
          </div>
        </div>

        {/* Field + Group settings — own scroll (capped to the viewport) so a long
            panel never gets cut off or moves the document with the page scroll.
            Modern overlay scrollbar: transparent track, no arrow buttons, floating
            rounded thumb (matches the Leads page). */}
        <div className="sticky top-4 max-h-[calc(100vh-176px)] w-72 shrink-0 space-y-4 overflow-y-auto pr-0.5 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-button]:hidden [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-muted-foreground/50 hover:[&::-webkit-scrollbar-thumb]:bg-muted-foreground/70">
          {/* Field Settings */}
          <div className="rounded-xl border border-border bg-card p-4">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="m-0 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Field Settings
              </h3>
              {selectedIds.length >= 1 && (
                <button
                  type="button"
                  onClick={removeSelected}
                  className="-mr-1 rounded-md p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                  aria-label="Delete selected"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
            {selectedIds.length >= 2 ? (
              <p className="text-sm text-muted-foreground">
                {selectedIds.length} fields selected. Pick a single field to edit it, or use
                Group Settings below.
              </p>
            ) : !selected ? (
              <p className="text-sm text-muted-foreground">Select a single field to edit.</p>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                  {(() => {
                    const TypeIcon = fieldTypeMeta(selected.type).icon;
                    return (
                      <TypeIcon
                        className={cn('h-3.5 w-3.5', fieldTypeMeta(selected.type).color)}
                      />
                    );
                  })()}
                  {fieldTypeMeta(selected.type).label} field
                </div>

                <Section title="Content" tone={fieldTypeMeta(selected.type).color}>
                <label className="block text-xs font-medium text-muted-foreground">
                  Label
                  <input
                    value={selected.label || ''}
                    onChange={(e) => update(selected.fieldId, { label: e.target.value })}
                    className="mt-1 h-9 w-full rounded-md border border-input bg-background px-2 text-sm text-foreground"
                  />
                </label>

                {selected.type === 'static_text' && (
                  <label className="block text-xs font-medium text-muted-foreground">
                    Fixed value
                    <input
                      value={selected.staticValue || ''}
                      onChange={(e) =>
                        update(selected.fieldId, { staticValue: e.target.value })
                      }
                      className="mt-1 h-9 w-full rounded-md border border-input bg-background px-2 text-sm text-foreground"
                    />
                  </label>
                )}

                {selected.type === 'static_signature' && (
                  <div className="space-y-2">
                    <span className="block text-xs font-medium text-muted-foreground">
                      Signature
                    </span>
                    {selected.staticValue ? (
                      <div className="rounded-md border border-border bg-background p-2">
                        <img
                          src={selected.staticValue}
                          alt="signature"
                          className="mx-auto max-h-16 object-contain"
                        />
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground">No signature set yet.</p>
                    )}
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setSigModalOpen(true)}
                        className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-md border border-input bg-card px-2 py-1.5 text-xs font-medium text-foreground hover:bg-accent"
                      >
                        <PenLine className="h-3.5 w-3.5" />
                        {selected.staticValue ? 'Replace' : 'Set signature'}
                      </button>
                      {selected.staticValue ? (
                        <button
                          type="button"
                          onClick={() => update(selected.fieldId, { staticValue: '' })}
                          className="rounded-md px-2 py-1.5 text-xs font-medium text-destructive hover:bg-destructive/10"
                        >
                          Clear
                        </button>
                      ) : null}
                    </div>
                  </div>
                )}
                </Section>

                {TEXTUAL_TYPES.has(selected.type) && (
                  <Section title="Display" tone={fieldTypeMeta(selected.type).color}>
                  <div className="flex gap-2">
                    <label className="block flex-1 text-xs font-medium text-muted-foreground">
                      Font size
                      <input
                        type="number"
                        min="4"
                        max="72"
                        value={selected.fontSize || 11}
                        onChange={(e) =>
                          update(selected.fieldId, { fontSize: Number(e.target.value) })
                        }
                        // Clamp on blur (not per keystroke, so typing "12" doesn't
                        // snap at "1"). 4–72 matches the server's burn clamp; a
                        // cleared input (0) falls back to the 11pt default.
                        onBlur={(e) => {
                          const n = Number(e.target.value) || 11;
                          update(selected.fieldId, {
                            fontSize: Math.max(4, Math.min(72, n)),
                          });
                        }}
                        className="mt-1 h-9 w-full rounded-md border border-input bg-background px-2 text-sm text-foreground"
                      />
                    </label>
                    <label className="block flex-1 text-xs font-medium text-muted-foreground">
                      Align
                      <select
                        value={selected.align || 'left'}
                        onChange={(e) => update(selected.fieldId, { align: e.target.value })}
                        className="mt-1 h-9 w-full rounded-md border border-input bg-background px-2 text-sm text-foreground"
                      >
                        <option value="left">Left</option>
                        <option value="center">Center</option>
                        <option value="right">Right</option>
                      </select>
                    </label>
                  </div>
                  </Section>
                )}

                {selected.type === 'digits' && (
                  <Section title="Boxes" tone={fieldTypeMeta(selected.type).color}>
                    <DigitBoxSettings
                      p={selected}
                      onChange={(patch) => update(selected.fieldId, patch)}
                    />
                  </Section>
                )}

                {selected.type === 'date' && (
                  <Section title="Date" tone={fieldTypeMeta(selected.type).color}>
                    {/* Nic 2026-10-21: an "effective as of" date the client
                        never fills in. Shown while they read, stamped with
                        the day they sign. */}
                    <label className="flex items-start gap-2 text-xs text-foreground">
                      <input
                        type="checkbox"
                        className="mt-0.5"
                        checked={!!selected.autoToday}
                        onChange={(e) =>
                          update(selected.fieldId, {
                            autoToday: e.target.checked ? true : undefined,
                            ...(e.target.checked ? { required: false, requiredIf: undefined } : {}),
                          })
                        }
                      />
                      <span>
                        <span className="font-medium">Today's date</span>
                        <span className="block text-[11px] text-muted-foreground">
                          Fills itself. They see today's date while reading and never type it.
                        </span>
                      </span>
                    </label>
                  </Section>
                )}

                {(selected.type === 'text' || selected.type === 'number') && (
                  <Section title="Validation" tone={fieldTypeMeta(selected.type).color}>
                  <div className="space-y-2">
                    <label className="block text-xs font-medium text-muted-foreground">
                      Overflow
                      <select
                        value={
                          selected.autoShrink
                            ? 'shrink'
                            : selected.limitToBox || selected.maxLength != null
                              ? 'limit'
                              : 'none'
                        }
                        onChange={(e) => {
                          const mode = e.target.value;
                          update(selected.fieldId, {
                            autoShrink: mode === 'shrink' ? true : undefined,
                            // Limiting defaults to fitting the box.
                            limitToBox: mode === 'limit' ? true : undefined,
                            maxLength: undefined,
                          });
                        }}
                        className="mt-1 h-9 w-full rounded-md border border-input bg-background px-2 text-sm text-foreground"
                      >
                        <option value="none">Allow overflow</option>
                        <option value="limit">Limit characters</option>
                        <option value="shrink">Shrink to fit</option>
                      </select>
                    </label>

                    {(selected.limitToBox || selected.maxLength != null) && (
                      <label className="block text-xs font-medium text-muted-foreground">
                        Limit by
                        <select
                          value={selected.limitToBox ? 'box' : 'number'}
                          onChange={(e) => {
                            const by = e.target.value;
                            update(selected.fieldId, {
                              limitToBox: by === 'box' ? true : undefined,
                              maxLength:
                                by === 'number'
                                  ? selected.maxLength != null
                                    ? selected.maxLength
                                    : 20
                                  : undefined,
                            });
                          }}
                          className="mt-1 h-9 w-full rounded-md border border-input bg-background px-2 text-sm text-foreground"
                        >
                          <option value="box">Box size (auto)</option>
                          <option value="number">Number</option>
                        </select>
                      </label>
                    )}

                    {selected.maxLength != null && (
                      <label className="block text-xs font-medium text-muted-foreground">
                        Max characters
                        <input
                          type="number"
                          min="1"
                          value={selected.maxLength}
                          onChange={(e) =>
                            update(selected.fieldId, {
                              maxLength: Math.max(1, Number(e.target.value) || 1),
                            })
                          }
                          className="mt-1 h-9 w-full rounded-md border border-input bg-background px-2 text-sm text-foreground"
                        />
                      </label>
                    )}

                    <label className="flex items-start gap-2 pt-1 text-xs font-medium text-muted-foreground">
                      <input
                        type="checkbox"
                        className="mt-0.5"
                        checked={Boolean(selected.sensitive)}
                        onChange={(e) =>
                          update(selected.fieldId, {
                            sensitive: e.target.checked || undefined,
                          })
                        }
                      />
                      <span>
                        Sensitive
                        <span className="mt-0.5 block font-normal text-muted-foreground/70">
                          Do not store in DB
                        </span>
                      </span>
                    </label>
                  </div>
                  </Section>
                )}

                {(() => {
                  const isAuto =
                    selected.type === 'static_text' ||
                    selected.type === 'signature_date' ||
                    selected.type === 'static_signature' ||
                    (selected.type === 'date' && !!selected.autoToday);
                  // Static/auto fields can't be required and (with no triggers) have
                  // no logic at all.
                  if (isAuto && placements.length <= 1) return null;
                  const reqLinking =
                    linking?.kind === 'field' &&
                    linking.id === selected.fieldId &&
                    linking.rule === 'requiredIf';
                  // One unified "Required" control: Optional / Always / When a
                  // trigger is met. (A field that's required but hidden by its
                  // Visibility rule is automatically not enforced.)
                  const requiredMode = selected.required
                    ? 'always'
                    : selected.requiredIf || reqLinking
                      ? 'when'
                      : 'optional';
                  return (
                    <Section title="Logic" tone={fieldTypeMeta(selected.type).color}>
                      {!isAuto && (
                        <div className="space-y-1">
                          <span className="block text-[11px] font-medium text-muted-foreground">
                            Required
                          </span>
                          <select
                            value={requiredMode}
                            onChange={(e) => {
                              const m = e.target.value;
                              if (m === 'optional') {
                                update(selected.fieldId, { required: undefined, requiredIf: undefined });
                              } else if (m === 'always') {
                                update(selected.fieldId, { required: true, requiredIf: undefined });
                              } else {
                                update(selected.fieldId, { required: undefined });
                                if (!selected.requiredIf) {
                                  setLinking({ kind: 'field', id: selected.fieldId, rule: 'requiredIf' });
                                }
                              }
                            }}
                            className="h-8 w-full rounded-md border border-input bg-background px-2 text-xs text-foreground"
                          >
                            <option value="optional">Optional</option>
                            <option value="always">Always required</option>
                            {placements.length > 1 && <option value="when">Required when…</option>}
                          </select>
                          {requiredMode === 'when' && (
                            <RulePicker
                              summaryPrefix="Required when"
                              emptyText="Pick a trigger field"
                              rule={selected.requiredIf}
                              triggerLabel={triggerLabelFor(selected.requiredIf)}
                              isLinking={reqLinking}
                              onPick={() =>
                                setLinking({ kind: 'field', id: selected.fieldId, rule: 'requiredIf' })
                              }
                              onClear={() => update(selected.fieldId, { requiredIf: undefined })}
                            />
                          )}
                        </div>
                      )}

                      {placements.length > 1 && (
                        <div className="space-y-1">
                          <span className="block text-[11px] font-medium text-muted-foreground">
                            Visibility
                          </span>
                          <RulePicker
                            summaryPrefix="Enabled when"
                            emptyText="Always shown"
                            rule={selected.enabledIf}
                            triggerLabel={triggerLabelFor(selected.enabledIf)}
                            isLinking={
                              linking?.kind === 'field' &&
                              linking.id === selected.fieldId &&
                              linking.rule === 'enabledIf'
                            }
                            onPick={() =>
                              setLinking({ kind: 'field', id: selected.fieldId, rule: 'enabledIf' })
                            }
                            onClear={() => update(selected.fieldId, { enabledIf: undefined })}
                          />
                        </div>
                      )}
                    </Section>
                  );
                })()}
              </div>
            )}
          </div>

          {/* Group Settings */}
          <div className="rounded-xl border border-border bg-card p-4">
            <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Group Settings
            </h3>
            {selected && selected.group ? (
              <GroupSettings
                info={groupInfo(selected.group)}
                onRename={(n) => renameGroup(selected.group, n)}
                onToggleSingle={(on) => setGroupSingle(selected.group, on)}
                onSelectAll={() => selectGroup(selected.group)}
                triggerLabel={triggerLabelFor(groupInfo(selected.group).enabledIf)}
                isLinking={
                  linking?.kind === 'group' &&
                  linking.id === selected.group &&
                  linking.rule === 'enabledIf'
                }
                onPickTrigger={() =>
                  setLinking({ kind: 'group', id: selected.group, rule: 'enabledIf' })
                }
                onClearTrigger={() =>
                  applyToGroup(selected.group, () => ({ enabledIf: undefined }))
                }
                onToggleAutoAdvance={(on) =>
                  applyToGroup(selected.group, (p) => ({
                    autoAdvance:
                      on && (p.type === 'text' || p.type === 'number') ? true : undefined,
                  }))
                }
                onToggleRequireOneTyped={(on) =>
                  applyToGroup(selected.group, (p) => ({
                    groupRequired: TYPED_REQUIRE_ONE.has(p.type)
                      ? on || undefined
                      : p.groupRequired,
                  }))
                }
                onToggleRequireOne={(on) =>
                  applyToGroup(selected.group, (p) => ({
                    groupRequired:
                      p.type === 'checkbox' || p.type === 'radio'
                        ? on || undefined
                        : p.groupRequired,
                  }))
                }
                selectedFieldId={selected.fieldId}
                onSelectMember={(id) => setSelectedIds([id])}
                onReorder={(f, t) => reorderGroup(selected.group, f, t)}
                onAddField={() => setAddingToGroup(selected.group)}
                isAddingField={addingToGroup === selected.group}
                footer={
                  <button
                    type="button"
                    onClick={() => removeFromGroup(selected.fieldId)}
                    className="w-full rounded-md border border-input bg-card px-2 py-1.5 text-xs font-medium text-destructive hover:bg-destructive/10"
                  >
                    Remove this field from group
                  </button>
                }
              />
            ) : selectedIds.length >= 2 && selSharedGroup ? (
              <GroupSettings
                info={groupInfo(selSharedGroup)}
                onRename={(n) => renameGroup(selSharedGroup, n)}
                onToggleSingle={(on) => setGroupSingle(selSharedGroup, on)}
                onSelectAll={() => selectGroup(selSharedGroup)}
                triggerLabel={triggerLabelFor(groupInfo(selSharedGroup).enabledIf)}
                isLinking={
                  linking?.kind === 'group' &&
                  linking.id === selSharedGroup &&
                  linking.rule === 'enabledIf'
                }
                onPickTrigger={() =>
                  setLinking({ kind: 'group', id: selSharedGroup, rule: 'enabledIf' })
                }
                onClearTrigger={() =>
                  applyToGroup(selSharedGroup, () => ({ enabledIf: undefined }))
                }
                onToggleAutoAdvance={(on) =>
                  applyToGroup(selSharedGroup, (p) => ({
                    autoAdvance:
                      on && (p.type === 'text' || p.type === 'number') ? true : undefined,
                  }))
                }
                onToggleRequireOneTyped={(on) =>
                  applyToGroup(selSharedGroup, (p) => ({
                    groupRequired: TYPED_REQUIRE_ONE.has(p.type)
                      ? on || undefined
                      : p.groupRequired,
                  }))
                }
                onToggleRequireOne={(on) =>
                  applyToGroup(selSharedGroup, (p) => ({
                    groupRequired:
                      p.type === 'checkbox' || p.type === 'radio'
                        ? on || undefined
                        : p.groupRequired,
                  }))
                }
                selectedFieldId={null}
                onSelectMember={(id) => setSelectedIds([id])}
                onReorder={(f, t) => reorderGroup(selSharedGroup, f, t)}
                onAddField={() => setAddingToGroup(selSharedGroup)}
                isAddingField={addingToGroup === selSharedGroup}
                footer={
                  <button
                    type="button"
                    onClick={() => ungroupGroup(selSharedGroup)}
                    className="w-full rounded-md border border-input bg-card px-2 py-1.5 text-xs font-medium text-destructive hover:bg-destructive/10"
                  >
                    Ungroup all
                  </button>
                }
              />
            ) : selectedIds.length >= 2 ? (
              <div className="space-y-1.5">
                <button
                  type="button"
                  onClick={groupSelected}
                  className="w-full rounded-md bg-primary px-2 py-1.5 text-xs font-semibold text-primary-foreground hover:opacity-90"
                >
                  Group {selectedIds.length} fields
                </button>
                <p className="text-[11px] leading-snug text-muted-foreground">
                  Tie these together to apply logic (single-choice now; show/hide later).
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  Select multiple fields to create a group
                  {groupList.length > 0 ? ', or pick an existing one:' : '.'}
                </p>
                {groupList.length > 0 && (
                  <div className="space-y-1">
                    {groupList.map((g) => (
                      <button
                        key={g.gid}
                        type="button"
                        onClick={() => selectGroup(g.gid)}
                        title="Select every field in this group"
                        className="flex w-full items-center gap-2.5 rounded-lg border border-transparent px-2 py-2 text-left transition-colors hover:border-border hover:bg-accent"
                      >
                        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                          <Layers className="h-3.5 w-3.5" />
                        </span>
                        <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
                          {g.name}
                        </span>
                        <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground/50" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentTemplateBuilder;
